import { RefObject } from 'react';
import { Controller, Path, useWatch } from 'react-hook-form';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  TextField,
} from '@mui/material';
import { z } from 'zod';
import { MIN_DISCHARGE_DATE, ResponsiveFormRow } from '@ldt/features/orders';
import { FormSectionCard } from '@ldt/features/orders/components/form_section_card';
import { FormDatePicker } from 'src/components/date_picker';
import {
  SquareCheckedIcon,
  SquareUncheckedIcon,
} from 'src/components/multi_select_checkbox_group';
import { RadioGroupField } from 'src/components/radio_group';
import { SectionHeader } from 'src/components/section_header';
import {
  FormFieldProps,
  PATIENT_STATUS_OPTIONS,
} from 'src/distribution_platforms/features/order_form';
import {
  PatientStatusAtBiopsy,
  MetastaticDiagnosisCode,
  PrimaryDiagnosisCode,
  SpecimenType,
} from 'src/types';
import { WorkflowType } from 'src/types/workflows';

const PATIENT_STATUS_VALUES = Object.values(PatientStatusAtBiopsy) as [
  string,
  ...string[],
];

const createBaseBillingSchema = (workflowType: WorkflowType) =>
  z.object({
    // Consent to Bill Patient - only required for ancillary organizations
    ...(workflowType === WorkflowType.ANCILLARY_LAB
      ? {
          consentToBillPatient: z.enum(['Yes', 'No'], {
            error: 'Consent to Bill Patient is required',
          }),
        }
      : {}),

    patientStatus: z.enum(PATIENT_STATUS_VALUES, {
      error: 'Patient Status is required',
    }),

    // Date of Discharge (Billing) - declared here but conditionally validated
    // in the custom resolver so we can access sibling fields like patientStatus.
    dateOfDischarge: z.string().optional(),

    // ICD‑10 Primary Diagnosis Codes - only real backend values (C61).
    primaryDiagnosisCodes: z.array(z.nativeEnum(PrimaryDiagnosisCode)),

    // UI-only flag: whether the "Other" checkbox is checked.
    // When true, customPrimaryDiagnosisCode is required (validated in custom resolver).
    isOtherDiagnosisCodeSelected: z.boolean(),

    customPrimaryDiagnosisCode: z
      .string()
      .max(10, 'Custom primary diagnosis code must be at most 10 characters')
      .optional(),
  });

const createMetastaticBillingSchema = (workflowType: WorkflowType) =>
  createBaseBillingSchema(workflowType).extend({
    // ICD-10 Metastatic Diagnosis Codes - only real backend values (C79.51).
    metastaticDiagnosisCodes: z.array(z.nativeEnum(MetastaticDiagnosisCode)),

    // UI-only flag: whether the metastatic "Other" checkbox is checked.
    // When true, customMetastaticDiagnosisCode is required (validated in custom resolver).
    isOtherMetastaticDiagnosisCodeSelected: z.boolean(),

    customMetastaticDiagnosisCode: z
      .string()
      .max(10, 'Custom metastatic diagnosis code must be at most 10 characters')
      .optional(),
  });

export const BillingInformationFormSchema = (workflowType: WorkflowType) =>
  createBaseBillingSchema(workflowType);

export const MetastaticBillingInformationFormSchema = (
  workflowType: WorkflowType,
) => createMetastaticBillingSchema(workflowType);

type BillingInformationFormData = z.infer<
  ReturnType<typeof createBaseBillingSchema>
>;

const PATIENT_STATUS_LABELS = {
  [SpecimenType.ProstateBiopsy]: 'Patient Status (at the time of biopsy)',
  [SpecimenType.ProstatePostRp]: 'Patient Status (at the time of procedure)',
  [SpecimenType.MetastaticProstateBiopsy]:
    'Patient Status (at the time of biopsy)',
};

// TODO(joshm): Refactor using form context
export const BillingInformationFormSection = <
  T extends BillingInformationFormData,
>({
  control,
  errors,
  trigger,
  specimenType,
  patientStatus,
  dateOfDischargeFieldRef,
  customPrimaryDiagnosisCodeFieldRef,
  workflowType,
  clearCustomPrimaryDiagnosisCode,
  customMetastaticDiagnosisCodeFieldRef,
  clearCustomMetastaticDiagnosisCode,
}: FormFieldProps<T> & {
  specimenType: SpecimenType;
  patientStatus: string;
  dateOfDischargeFieldRef: RefObject<HTMLInputElement | null>;
  customPrimaryDiagnosisCodeFieldRef: RefObject<HTMLInputElement | null>;
  workflowType: WorkflowType;
  clearCustomPrimaryDiagnosisCode: () => void;
  // Optional: only used when specimenType is MetastaticProstateBiopsy
  customMetastaticDiagnosisCodeFieldRef?: RefObject<HTMLInputElement | null>;
  clearCustomMetastaticDiagnosisCode?: () => void;
}) => {
  const isOtherDiagnosisCodeSelected = useWatch({
    control,
    name: 'isOtherDiagnosisCodeSelected' as Path<T>,
  });
  const isOtherMetastaticDiagnosisCodeSelected = useWatch({
    control,
    name: 'isOtherMetastaticDiagnosisCodeSelected' as Path<T>,
  });

  if (
    ![
      SpecimenType.ProstateBiopsy,
      SpecimenType.ProstatePostRp,
      SpecimenType.MetastaticProstateBiopsy,
    ].includes(specimenType)
  ) {
    return null;
  }

  const patientStatusLabel =
    PATIENT_STATUS_LABELS[specimenType as keyof typeof PATIENT_STATUS_LABELS];

  return (
    <FormSectionCard header={<SectionHeader title='Billing Information' />}>
      <Box
        data-testid='billing-information-section'
        component='span'
        sx={{ display: 'contents' }}
      >
        <ResponsiveFormRow>
          <Box
            sx={{
              flex: 1,
            }}
          >
            <Box data-testid='billing-patient-status'>
              <Controller
                name={'patientStatus' as Path<T>}
                control={control}
                render={({ field }) => (
                  <RadioGroupField
                    {...field}
                    required
                    row
                    name={field.name}
                    label={patientStatusLabel}
                    value={field.value as string | undefined}
                    options={PATIENT_STATUS_OPTIONS}
                    validationText={
                      errors.patientStatus?.message?.toString() ?? ''
                    }
                    onChange={async (event) => {
                      field.onChange(event);
                      if (event.target.value) {
                        await trigger?.('patientStatus' as Path<T>);
                      }
                    }}
                  />
                )}
              />
            </Box>

            {/* Date of Discharge - required only for Hospital In/Outpatient */}
            {(patientStatus === PatientStatusAtBiopsy.HospitalInpatient ||
              patientStatus === PatientStatusAtBiopsy.HospitalOutpatient) && (
              <Controller
                name={'dateOfDischarge' as Path<T>}
                control={control}
                render={({ field }) => (
                  <Box data-testid='billing-date-of-discharge'>
                    <FormDatePicker
                      label='Date of Discharge'
                      minDate={MIN_DISCHARGE_DATE}
                      maxDate={new Date()}
                      value={field.value as string | null | undefined}
                      required
                      fullWidth
                      error={!!errors.dateOfDischarge}
                      helperText={
                        errors.dateOfDischarge?.message?.toString() ?? ' '
                      }
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      slotProps={{
                        textField: { inputRef: dateOfDischargeFieldRef },
                      }}
                    />
                  </Box>
                )}
              />
            )}

            {/* Consent to Bill Patient - only shown for distributed LIMS workflow */}
            {workflowType === WorkflowType.ANCILLARY_LAB && (
              <Box data-testid='billing-consent-to-bill-patient'>
                <Controller
                  name={'consentToBillPatient' as Path<T>}
                  control={control}
                  render={({ field }) => (
                    <RadioGroupField
                      {...field}
                      required
                      row
                      name={field.name}
                      label='Consent to Bill Patient'
                      options={[
                        { value: 'Yes', name: 'Yes' },
                        { value: 'No', name: 'No' },
                      ]}
                      validationText={
                        errors.consentToBillPatient?.message?.toString() ?? ''
                      }
                      value={field.value as string | undefined}
                      onChange={async (event) => {
                        field.onChange(event);
                        if (event.target.value) {
                          await trigger?.('consentToBillPatient' as Path<T>);
                        }
                      }}
                    />
                  )}
                />
              </Box>
            )}
          </Box>

          {/* ICD‑10 Primary Diagnosis Code(s) */}
          <Box
            sx={{
              flex: 1,
            }}
          >
            <Box data-testid='billing-primary-diagnosis-codes'>
              <FormControl
                required
                fullWidth
                component='fieldset'
                error={!!errors.primaryDiagnosisCodes}
              >
                <FormLabel
                  required
                  component='legend'
                  error={!!errors.primaryDiagnosisCodes}
                >
                  ICD-10 Primary Diagnosis Code(s)
                </FormLabel>
                <FormGroup
                  row
                  sx={{
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 0,
                    '& .MuiFormControlLabel-root': {
                      mr: 2,
                      mb: 0,
                      minWidth: 'auto',
                    },
                  }}
                >
                  {/* C61 - a real backend PrimaryDiagnosisCode */}
                  <Controller
                    name={'primaryDiagnosisCodes' as Path<T>}
                    control={control}
                    render={({ field }) => {
                      const selectedCodes =
                        (field.value as PrimaryDiagnosisCode[] | undefined) ||
                        [];
                      return (
                        <FormControlLabel
                          label='C61'
                          control={
                            <Checkbox
                              icon={<SquareUncheckedIcon />}
                              checkedIcon={<SquareCheckedIcon />}
                              checked={selectedCodes.includes(
                                PrimaryDiagnosisCode.C61,
                              )}
                              onChange={async (_, checked) => {
                                const next = checked
                                  ? [...selectedCodes, PrimaryDiagnosisCode.C61]
                                  : selectedCodes.filter(
                                      (c) => c !== PrimaryDiagnosisCode.C61,
                                    );
                                field.onChange(next);
                                await trigger?.(
                                  'primaryDiagnosisCodes' as Path<T>,
                                );
                              }}
                            />
                          }
                        />
                      );
                    }}
                  />

                  {/* Other - UI-only; custom ICD-10 code required when checked */}
                  <Controller
                    name={'isOtherDiagnosisCodeSelected' as Path<T>}
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        label='Other'
                        control={
                          <Checkbox
                            icon={<SquareUncheckedIcon />}
                            checkedIcon={<SquareCheckedIcon />}
                            checked={field.value as boolean}
                            onChange={async (_, checked) => {
                              field.onChange(checked);
                              if (!checked) {
                                clearCustomPrimaryDiagnosisCode();
                                await trigger?.(
                                  'customPrimaryDiagnosisCode' as Path<T>,
                                );
                              }
                              await trigger?.(
                                'primaryDiagnosisCodes' as Path<T>,
                              );
                            }}
                          />
                        }
                      />
                    )}
                  />
                </FormGroup>
                <Box sx={{ minHeight: '20px', mt: 0 }}>
                  <FormHelperText error={!!errors.primaryDiagnosisCodes}>
                    {errors.primaryDiagnosisCodes?.message?.toString() || ' '}
                  </FormHelperText>
                </Box>
              </FormControl>

              {/* Custom code text field shown when "Other" is checked */}
              {isOtherDiagnosisCodeSelected && (
                <Controller
                  name={'customPrimaryDiagnosisCode' as Path<T>}
                  control={control}
                  render={({ field: customField }) => (
                    <TextField
                      {...customField}
                      fullWidth
                      required
                      label='Custom ICD-10 Code'
                      onBlur={customField.onBlur}
                      onChange={customField.onChange}
                      inputRef={customPrimaryDiagnosisCodeFieldRef}
                      slotProps={{
                        input: {
                          inputProps: {
                            maxLength: 10,
                          },
                        },
                      }}
                      error={!!errors.customPrimaryDiagnosisCode}
                      helperText={
                        errors.customPrimaryDiagnosisCode?.message?.toString() ??
                        ' '
                      }
                    />
                  )}
                />
              )}
            </Box>
          </Box>
        </ResponsiveFormRow>

        {/* ICD-10 Metastatic Diagnosis Code(s) — MetastaticProstateBiopsy only */}
        {specimenType === SpecimenType.MetastaticProstateBiopsy && (
          <ResponsiveFormRow>
            <Box
              sx={{
                flex: 1,
              }}
            >
              <Box data-testid='billing-metastatic-diagnosis-codes'>
                <FormControl
                  required
                  fullWidth
                  component='fieldset'
                  error={
                    !!(errors as Record<string, { message?: string }>)
                      .metastaticDiagnosisCodes
                  }
                >
                  <FormLabel
                    required
                    component='legend'
                    error={
                      !!(errors as Record<string, { message?: string }>)
                        .metastaticDiagnosisCodes
                    }
                  >
                    ICD-10 Metastatic Diagnosis Code(s)
                  </FormLabel>
                  <FormGroup
                    row
                    sx={{
                      flexWrap: 'wrap',
                      gap: 1,
                      mb: 0,
                      '& .MuiFormControlLabel-root': {
                        mr: 2,
                        mb: 0,
                        minWidth: 'auto',
                      },
                    }}
                  >
                    {/* C79.51 - a real backend MetastaticDiagnosisCode */}
                    <Controller
                      name={'metastaticDiagnosisCodes' as Path<T>}
                      control={control}
                      render={({ field }) => {
                        const selectedCodes =
                          (field.value as
                            | MetastaticDiagnosisCode[]
                            | undefined) || [];
                        return (
                          <FormControlLabel
                            label='C79.51'
                            control={
                              <Checkbox
                                icon={<SquareUncheckedIcon />}
                                checkedIcon={<SquareCheckedIcon />}
                                checked={selectedCodes.includes(
                                  MetastaticDiagnosisCode.C79_51,
                                )}
                                onChange={async (_, checked) => {
                                  const next = checked
                                    ? [
                                        ...selectedCodes,
                                        MetastaticDiagnosisCode.C79_51,
                                      ]
                                    : selectedCodes.filter(
                                        (c) =>
                                          c !== MetastaticDiagnosisCode.C79_51,
                                      );
                                  field.onChange(next);
                                  await trigger?.(
                                    'metastaticDiagnosisCodes' as Path<T>,
                                  );
                                }}
                              />
                            }
                          />
                        );
                      }}
                    />

                    {/* Other - UI-only; custom metastatic code required when checked */}
                    <Controller
                      name={'isOtherMetastaticDiagnosisCodeSelected' as Path<T>}
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          label='Other'
                          control={
                            <Checkbox
                              icon={<SquareUncheckedIcon />}
                              checkedIcon={<SquareCheckedIcon />}
                              checked={field.value as boolean}
                              onChange={async (_, checked) => {
                                field.onChange(checked);
                                if (!checked) {
                                  clearCustomMetastaticDiagnosisCode?.();
                                  await trigger?.(
                                    'customMetastaticDiagnosisCode' as Path<T>,
                                  );
                                }
                                await trigger?.(
                                  'metastaticDiagnosisCodes' as Path<T>,
                                );
                              }}
                            />
                          }
                        />
                      )}
                    />
                  </FormGroup>
                  <Box sx={{ minHeight: '20px', mt: 0 }}>
                    <FormHelperText
                      error={
                        !!(errors as Record<string, { message?: string }>)
                          .metastaticDiagnosisCodes
                      }
                    >
                      {(errors as Record<string, { message?: string }>)
                        .metastaticDiagnosisCodes?.message || ' '}
                    </FormHelperText>
                  </Box>
                </FormControl>

                {/* Custom metastatic code text field shown when "Other" is checked */}
                {isOtherMetastaticDiagnosisCodeSelected && (
                  <Controller
                    name={'customMetastaticDiagnosisCode' as Path<T>}
                    control={control}
                    render={({ field: customField }) => (
                      <TextField
                        {...customField}
                        fullWidth
                        required
                        label='Custom ICD-10 Metastatic Code'
                        onBlur={customField.onBlur}
                        onChange={customField.onChange}
                        inputRef={customMetastaticDiagnosisCodeFieldRef}
                        slotProps={{
                          input: {
                            inputProps: {
                              maxLength: 10,
                            },
                          },
                        }}
                        error={
                          !!(errors as Record<string, { message?: string }>)
                            .customMetastaticDiagnosisCode
                        }
                        helperText={
                          (errors as Record<string, { message?: string }>)
                            .customMetastaticDiagnosisCode?.message ?? ' '
                        }
                      />
                    )}
                  />
                )}
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
              }}
            />
          </ResponsiveFormRow>
        )}
      </Box>
    </FormSectionCard>
  );
};
